import { useEffect, useMemo, useState } from "react";
import { BsCashStack } from "react-icons/bs";
import { FiFileText, FiTrendingUp } from "react-icons/fi";
import { LuPencil } from "react-icons/lu";
import { MdOutlineCalculate } from "react-icons/md";
import { RiDeleteBinLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import UserAvatar from "../../../components/UserAvatar";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import { hasPermission } from "../../../utils/helper";
import { plusIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const buildStatIcon = (icon) => <span className="finance_metric_icon">{icon}</span>;

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toText = (value) => String(value ?? "").trim();

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getTripCostEstimationOverview = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (overview, row) => ({
      totalEstimations: overview.totalEstimations + 1,
      estimatedRevenue:
        overview.estimatedRevenue + toNumber(row?.estimatedRevenue),
      estimatedCost: overview.estimatedCost + toNumber(row?.estimatedCost),
      projectedProfit:
        overview.projectedProfit + toNumber(row?.projectedProfit),
    }),
    {
      totalEstimations: 0,
      estimatedRevenue: 0,
      estimatedCost: 0,
      projectedProfit: 0,
    }
  );

const formatDateOnly = (value) => {
  const rawValue = toText(value);
  if (!rawValue) return "-";

  const directDateMatch = rawValue.match(/^\d{4}-\d{2}-\d{2}/);
  if (directDateMatch) {
    return directDateMatch[0];
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue;

  return parsedDate.toISOString().slice(0, 10);
};

const buildRouteMeta = (item = {}) => {
  const startLocation = toText(
    item?.start_trip?.location || item?.trip?.start_trip?.location
  );
  const endLocation = toText(
    item?.end_trip?.location || item?.trip?.end_trip?.location
  );
  const fallbackRoute = toText(
    item?.route || item?.route_label || item?.trip_route
  );
  const routeLabel =
    [startLocation, endLocation].filter(Boolean).join(" -> ") || fallbackRoute;
  const route = routeLabel ? routeLabel.replace(/\s*->\s*/g, " > ") : "-";

  return {
    routeId: routeLabel || route || "",
    routeLabel,
    route,
  };
};

const normalizeTripCostEstimationApiRow = (item = {}, index = 0) => {
  const routeMeta = buildRouteMeta(item);
  const distance = toNumber(item?.distance);
  const maintenanceCost = toNumber(item?.maintenance_cost);
  const maintenanceCostPerKm =
    distance > 0 ? Number((maintenanceCost / distance).toFixed(6)) : 0;
  const tripRecordId = Number(item?.trip_id);
  const creatorName = toText(
    item?.creator?.username ||
      item?.creator?.name ||
      item?.creator?.full_name ||
      item?.created_by
  );

  return {
    id: item?.id ?? `${item?.trip_cost_estimate_id || "tce"}-${index}`,
    estimationId:
      toText(item?.trip_cost_estimate_id) || `TCE-${String(item?.id || index)}`,
    tripId: toText(item?.trip?.trip_id || item?.trip_id),
    tripRecordId: Number.isFinite(tripRecordId) ? tripRecordId : "",
    vehicleId: toText(
      item?.trip?.vehicle?.registration_number ||
        item?.vehicle?.registration_number ||
        item?.vehicle_id
    ),
    driverId: toText(item?.primary_driver?.id || item?.creator?.id || ""),
    driverName: toText(item?.primary_driver?.username || item?.creator?.username),
    driverImage: toText(item?.primary_driver?.image || item?.creator?.image),
    routeId: routeMeta.routeId,
    routeLabel: routeMeta.routeLabel,
    route: routeMeta.route,
    distance,
    tripDuration: toText(item?.duration),
    fuelPricePerLiter: toNumber(item?.fuel_price_per_ltr),
    vehicleFuelEfficiency: toNumber(item?.fuel_efficiency_per_ltr),
    foodAllowancePerDay: toNumber(item?.driver_allowance),
    maintenanceCostPerKm,
    estimatedTollCost: toNumber(item?.toll_cost),
    estimatedRevenue: toNumber(item?.estimated_total_revenue),
    estimatedCost: toNumber(item?.estimated_total_cost),
    projectedProfit: toNumber(item?.estimated_total_profit),
    notes: toText(item?.note),
    attachments: safeArray(item?.files),
    createdDate: formatDateOnly(item?.created_at || item?.updated_at),
    createdBy: creatorName || "-",
    createdByImage: toText(item?.creator?.image),
  };
};

const TripCostEstimation = () => {
  const navigate = useNavigate();
  const canCreateEstimation = hasPermission({
    permissionSlug: FinancePermission.Create,
    menuSlug: FinanceSubModules.TripCostEstimation,
    moduleSlug: AppMainModules.finance,
  });
  const canUpdateEstimation = hasPermission({
    permissionSlug: FinancePermission.Update,
    menuSlug: FinanceSubModules.TripCostEstimation,
    moduleSlug: AppMainModules.finance,
  });
  const canDeleteEstimation = hasPermission({
    permissionSlug: FinancePermission.Delete,
    menuSlug: FinanceSubModules.TripCostEstimation,
    moduleSlug: AppMainModules.finance,
  });

  const [apiRows, setApiRows] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [locallyHiddenIds, setLocallyHiddenIds] = useState([]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    let isMounted = true;

    const fetchTripCostEstimations = async () => {
      setLoading(true);

      try {
        const response = await apiService.getTripCostEstimatePaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters,
        });

        const payload = response?.data || {};
        const nextRows = safeArray(payload?.results).map(
          normalizeTripCostEstimationApiRow
        );

        if (!isMounted) return;

        setApiRows(nextRows);
        setMeta({
          totalItems: Number(payload?.meta?.totalItems || payload?.total || nextRows.length),
          totalPages: Number(payload?.meta?.totalPages || 1),
        });
      } catch (error) {
        if (!isMounted) return;
        setApiRows([]);
        setMeta({ totalItems: 0, totalPages: 1 });
        errorNotify(error || "Failed to fetch trip cost estimations");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTripCostEstimations();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchText, searchFilters]);

  const rows = useMemo(
    () =>
      apiRows.filter(
        (row) => !locallyHiddenIds.includes(String(row?.id ?? ""))
      ),
    [apiRows, locallyHiddenIds]
  );

  const totalItems = Math.max(
    0,
    Number(meta?.totalItems || rows.length) - locallyHiddenIds.length
  );
  const totalPages = Math.max(1, Number(meta?.totalPages || 1));

  const selectedDeleteRow = useMemo(
    () => rows.find((row) => String(row.id) === String(deleteId || "")) || null,
    [deleteId, rows]
  );

  const overview = useMemo(() => getTripCostEstimationOverview(rows), [rows]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Estimations",
        value: formatNumber(totalItems),
        text: <span>Total estimation records</span>,
        icon: buildStatIcon(<FiFileText />),
      },
      {
        title: "Estimated Revenue (PKR)",
        value: formatNumber(overview.estimatedRevenue),
        text: <span>Sum of Estimated Revenues</span>,
        className: "completed",
        icon: buildStatIcon(<BsCashStack />),
      },
      {
        title: "Estimated Cost (PKR)",
        value: formatNumber(overview.estimatedCost),
        text: <span>Sum of Estimated Costs</span>,
        className: "late",
        icon: buildStatIcon(<MdOutlineCalculate />),
      },
      {
        title: "Projected Profit (PKR)",
        value: formatNumber(overview.projectedProfit),
        text: <span>Aggregate Projected Profit</span>,
        className: "pending-delivery",
        icon: buildStatIcon(<FiTrendingUp />),
      },
    ],
    [overview, totalItems]
  );

  const handleCreateClick = () => {
    navigate("/trip-cost-estimation/create");
  };

  const handleOpenDetails = (row) => {
    navigate(`/trip-cost-estimation/${row?.id}`, {
      state: { estimation: row },
    });
  };

  const handleEditClick = (row) => {
    handleOpenDetails(row);
  };

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (deleteId === null || deleteId === undefined) return;

    setDeleteLoading(true);

    try {
      await apiService.deleteTripCostEstimate(deleteId);
      setLocallyHiddenIds((previous) =>
        previous.includes(String(deleteId))
          ? previous
          : [...previous, String(deleteId)]
      );
      successNotify("Trip cost estimation deleted successfully");
      closeDeletePopup();
    } catch (error) {
      errorNotify(error || "Failed to delete trip cost estimation");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      name: "Estimation ID",
      key: "estimation_id",
      selector: (row) => (
        <button
          type="button"
          className="finance_link_button finance_link_text"
          onClick={() => handleOpenDetails(row)}
        >
          {row.estimationId}
        </button>
      ),
      tableColumn: true,
      filterName: "estimationIdFilter",
      type: "text",
      minWidth: "150px",
    },
    {
      name: "Trip ID",
      key: "trip_id",
      selector: (row) => <span className="finance_link_text">{row.tripId}</span>,
      tableColumn: true,
      filterName: "tripIdFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Vehicle ID",
      key: "vehicle_id",
      selector: (row) => (
        <span className="finance_link_text">{row.vehicleId || "-"}</span>
      ),
      tableColumn: true,
      filterName: "vehicleIdFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Route",
      key: "route",
      selector: (row) => row.route || "-",
      tableColumn: true,
      filterName: "routeFilter",
      type: "text",
      minWidth: "210px",
    },
    {
      name: "Estimated Revenue (PKR)",
      key: "estimated_revenue",
      selector: (row) => formatNumber(row.estimatedRevenue),
      tableColumn: true,
      filterName: "estimatedRevenueFilter",
      type: "number",
      minWidth: "190px",
    },
    {
      name: "Estimated Cost (PKR)",
      key: "estimated_cost",
      selector: (row) => formatNumber(row.estimatedCost),
      tableColumn: true,
      filterName: "estimatedCostFilter",
      type: "number",
      minWidth: "180px",
    },
    {
      name: "Projected Profit (PKR)",
      key: "projected_profit",
      selector: (row) => formatNumber(row.projectedProfit),
      tableColumn: true,
      filterName: "projectedProfitFilter",
      type: "number",
      minWidth: "185px",
    },
    {
      name: "Created Date",
      key: "created_date",
      selector: (row) => row.createdDate || "-",
      tableColumn: true,
      filterName: "createdDateFilter",
      type: "date",
      minWidth: "150px",
    },
    {
      name: "Created By",
      key: "created_by",
      selector: (row) => (
        <div className="finance_created_by_cell">
          <UserAvatar
            username={row.createdBy}
            image={row.createdByImage}
            marginRight="0px"
          />
          <span className="finance_created_by_name">{row.createdBy}</span>
        </div>
      ),
      tableColumn: true,
      filterName: "createdByFilter",
      type: "text",
      minWidth: "210px",
    },
    {
      name: "Action",
      key: "action",
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateEstimation && (
            <button
              type="button"
              className="edit_btn"
              title="Edit estimation"
              onClick={() => handleEditClick(row)}
            >
              <LuPencil />
            </button>
          )}
          {canDeleteEstimation && (
            <button
              type="button"
              className="delete_btn"
              title="Delete estimation"
              onClick={() => {
                setDeleteId(row.id);
                setDeletePopup(true);
              }}
            >
              <RiDeleteBinLine />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <div className="trip_cost_estimation_page">
      <Breadcrumbs values={["Finance", "Trip Cost Estimation"]} />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Are you sure you want to delete this estimation?"
        description={`Deleting "${selectedDeleteRow?.estimationId || "this estimation"}" will permanently remove it.`}
      />

      <div className="access_view">
        <h2>Trip Cost Estimation</h2>

        <div className="services_buttons">
          {canCreateEstimation && (
            <button type="button" onClick={handleCreateClick}>
              {plusIcon} Create Estimation
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} numberOfColumn={3} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={rows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={(nextLimit) => {
          setLimit(nextLimit);
          setCurrentPage(1);
        }}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[8, 10, 20, 50]}
        tableClassName="trip_cost_estimation_table parts_table_fix"
      />

      {!loading && totalItems === 0 && (
        <div className="add_trip_suggestion_div" style={{ marginTop: 0 }}>
          <h4>There is nothing here...</h4>
          <p>
            There is nothing to view right now, please add a trip cost estimation to
            get started.
          </p>
          {canCreateEstimation && (
            <Button
              submitText={
                <span className="d-flex align-items-center gap-2 justify-content-center">
                  {plusIcon} Create Estimation
                </span>
              }
              type="button"
              shouldRow={false}
              isShowSubmitBtn
              isShowCancel={false}
              submitBtn={handleCreateClick}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TripCostEstimation;
